import cn from 'classnames';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageOptions } from 'editor/src/store/variants/types';

import Input from 'editor/src/component/Input';

import clampPageCount from './clampPageCount';

import styles from './index.module.scss';

interface Props {
  pages: PageOptions;
  selectedPageCount: number;
  disabled: boolean;
  keepToGrid?: boolean;
  onChange: (pageCount: number) => void;
}

function PageCountInput({ pages, selectedPageCount, disabled, keepToGrid = false, onChange }: Props) {
  const { t } = useTranslation();
  const [value, setValue] = useState(`${selectedPageCount}`);

  // The page count also changes from outside the input (undo, product switch,
  // a design loaded with its own page count) — follow it.
  useEffect(() => setValue(`${selectedPageCount}`), [selectedPageCount]);

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    setValue(e.target.value);
  }

  function commit() {
    const pageCount = clampPageCount(value, pages, keepToGrid);
    if (pageCount === undefined) {
      setValue(`${selectedPageCount}`);
      return;
    }

    setValue(`${pageCount}`);
    if (pageCount !== selectedPageCount) {
      onChange(pageCount);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  }

  return (
    <>
      <Input
        className={cn('cy-page-count-input', { [styles.disabled]: disabled })}
        disabled={disabled}
        type="number"
        value={value}
        onChange={onInputChange}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      <div className={styles.hint}>{t('editor-page-count-range-hint', { min: pages.min, max: pages.max })}</div>
    </>
  );
}

export default React.memo(PageCountInput);
